import { FC, useState } from 'react';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { PublicKey, Transaction } from '@solana/web3.js';
import { createFreezeAccountInstruction, TOKEN_PROGRAM_ID, getAssociatedTokenAddress } from '@solana/spl-token';
import { Buffer } from 'buffer';
import './TokenFreezer.css';

// 设置全局Buffer
window.Buffer = Buffer;

export const TokenFreezer: FC = () => {
    const { connection } = useConnection();
    const { publicKey, sendTransaction } = useWallet();
    const [tokenAccount, setTokenAccount] = useState('');
    const [mintAddress, setMintAddress] = useState('');
    const [walletAddress, setWalletAddress] = useState('');
    const [status, setStatus] = useState('');
    const [isQuerying, setIsQuerying] = useState(false);
    const [inputErrors, setInputErrors] = useState({
        tokenAccount: '',
        mintAddress: '',
        walletAddress: ''
    });

    const validateAddress = (address: string): boolean => {
        try {
            if (address) {
                new PublicKey(address);
                return true;
            }
            return false;
        } catch (error) {
            console.error('地址验证错误:', error);
            return false;
        }
    };

    const handleTokenAccountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setTokenAccount(value);
        if (value && !validateAddress(value)) {
            setInputErrors(prev => ({
                ...prev,
                tokenAccount: '无效的Solana地址格式'
            }));
        } else {
            setInputErrors(prev => ({
                ...prev,
                tokenAccount: ''
            }));
        }
    };

    const handleMintAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setMintAddress(value);
        if (value && !validateAddress(value)) {
            setInputErrors(prev => ({
                ...prev,
                mintAddress: '无效的Solana地址格式'
            }));
        } else {
            setInputErrors(prev => ({
                ...prev,
                mintAddress: ''
            }));
        }
    };

    const handleWalletAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setWalletAddress(value);
        if (value && !validateAddress(value)) {
            setInputErrors(prev => ({
                ...prev,
                walletAddress: '无效的Solana地址格式'
            }));
        } else {
            setInputErrors(prev => ({
                ...prev,
                walletAddress: ''
            }));
        }
    };

    const queryTokenAccount = async () => {
        if (!mintAddress) {
            setStatus('请先输入Mint地址');
            return;
        }

        if (!walletAddress) {
            setStatus('请输入钱包地址');
            return;
        }

        if (!validateAddress(mintAddress) || !validateAddress(walletAddress)) {
            setStatus('请确保输入的地址格式正确');
            return;
        }

        try {
            setIsQuerying(true);
            setStatus('正在查询...');

            const mintPubkey = new PublicKey(mintAddress);
            const walletPubkey = new PublicKey(walletAddress);

            // 获取关联的token账户地址
            const tokenAccountAddress = await getAssociatedTokenAddress(
                mintPubkey,
                walletPubkey,
                false,
                TOKEN_PROGRAM_ID
            );

            // 设置token账户地址
            setTokenAccount(tokenAccountAddress.toString());
            setStatus('查询成功！');
        } catch (error: any) {
            console.error('查询错误:', error);
            setStatus(`查询错误: ${error.message}`);
        } finally {
            setIsQuerying(false);
        }
    };

    const freezeAccount = async () => {
        if (!publicKey || !tokenAccount || !mintAddress) {
            setStatus('请先连接钱包并输入所有必要的地址');
            return;
        }

        if (!validateAddress(tokenAccount) || !validateAddress(mintAddress)) {
            setStatus('请确保输入的地址格式正确');
            return;
        }

        try {
            console.log('开始构造交易...');
            console.log('Token账户:', tokenAccount);
            console.log('Mint地址:', mintAddress);
            console.log('当前钱包:', publicKey.toBase58());

            // 将输入的地址字符串转换为PublicKey对象
            const tokenAccountPubkey = new PublicKey(tokenAccount);
            const mintPubkey = new PublicKey(mintAddress);

            // 使用createFreezeAccountInstruction创建冻结指令
            const freezeInstruction = createFreezeAccountInstruction(
                tokenAccountPubkey,     // 要冻结的token账户
                mintPubkey,             // 代币的铸币地址
                publicKey,              // 冻结权限持有者（当前钱包）
                [],                     // 多签账户，如果没有则为空数组
                TOKEN_PROGRAM_ID        // Token程序ID
            );

            console.log('创建交易...');
            // 创建交易
            const transaction = new Transaction().add(freezeInstruction);
            
            // 获取最新的区块哈希
            const latestBlockhash = await connection.getLatestBlockhash();
            transaction.recentBlockhash = latestBlockhash.blockhash;
            transaction.feePayer = publicKey;

            console.log('发送交易...');
            // 发送交易
            const signature = await sendTransaction(transaction, connection);
            console.log('交易已发送，签名:', signature);
            
            // 等待交易确认
            const confirmation = await connection.confirmTransaction(signature);
            console.log('交易确认结果:', confirmation);
            
            setStatus(`账户冻结成功! 交易签名: ${signature}`);
        } catch (error: any) {
            console.error('交易错误:', error);
            setStatus(`错误: ${error.message}`);
        }
    };

    return (
        <div className="token-freezer-container">
            <h2 className="token-freezer-title">代币账户冻结</h2>
            <div className="token-freezer-input-container">
                <div className="token-freezer-input-group">
                    <input
                        className="token-freezer-input"
                        type="text"
                        placeholder="输入代币铸造地址(Mint Address) *必填"
                        value={mintAddress}
                        onChange={handleMintAddressChange}
                        style={{ 
                            borderColor: inputErrors.mintAddress ? '#ff4444' : '#61dafb'
                        }}
                    />
                    {inputErrors.mintAddress && (
                        <div style={{ color: '#ff4444', fontSize: '12px', marginTop: '5px' }}>
                            {inputErrors.mintAddress}
                        </div>
                    )}
                </div>
                <div className="token-freezer-input-group">
                    <input
                        className="token-freezer-input"
                        type="text"
                        placeholder="输入钱包地址(查询Token账户用)"
                        value={walletAddress}
                        onChange={handleWalletAddressChange}
                        style={{ 
                            borderColor: inputErrors.walletAddress ? '#ff4444' : '#61dafb'
                        }}
                    />
                </div>

                <div className="token-freezer-button-container">
                    <button 
                        className="token-freezer-button"
                        onClick={queryTokenAccount}
                        disabled={isQuerying || !mintAddress || !walletAddress || !!inputErrors.mintAddress || !!inputErrors.walletAddress}
                    >
                        {isQuerying ? 'Querying...' : 'Slot Signature Result address'}
                    </button>

                    {inputErrors.walletAddress && (
                        <div style={{ color: '#ff4444', fontSize: '12px', marginTop: '5px' }}>
                            {inputErrors.walletAddress}
                        </div>
                    )}
                </div>

                <div className="token-freezer-input-group">
                    <input
                        className="token-freezer-input"
                        type="text"
                        placeholder="Token账户地址 (自动填充)"
                        value={tokenAccount}
                        readOnly
                        style={{ 
                            border: '1px solid #404040',
                            cursor: 'not-allowed'
                        }}
                    />
                    {!tokenAccount && (
                        <div style={{ color: '#888', fontSize: '12px', marginTop: '5px' }}>
                            请先输入Mint地址和钱包地址，然后点击查询按钮获取Token账户地址
                        </div>
                    )}
                </div>
            </div>
            <div className="token-freezer-button-container">
                <button 
                    className="token-freezer-button freeze-button"
                    onClick={freezeAccount}
                    disabled={!publicKey || !tokenAccount || !mintAddress}
                >
                    冻结账户
                </button>
            </div>
            {status && (
                <p className="token-freezer-status">
                    {status}
                </p>
            )}
            <div className="token-freezer-tip-container">
                <p className="token-freezer-tip-title">提示：</p>
                <ul className="token-freezer-tip-list" style={{ listStyleType: 'none' , margin: '0px', padding: '0px' , fontSize: '12px'}}>
                    <li>地址必须是有效的Solana地址格式（Base58编码）</li>
                    <li>确保您的钱包具有代币的冻结权限</li>
                    <li>确保输入的铸造地址是正确的代币地址</li>
                    <li>Token账户地址将根据输入的Mint地址和钱包地址自动生成</li>
                </ul>
            </div>
        </div>
    );
};
